import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import ResourceModal from 'app/components/ResourceModal';
import ResourceModalPanel from 'app/components/ResourceModalPanel';
import CustomerUploadModal from 'app/components/Uploads/CustomerUploadModal';
import useMomentsUploadForm from 'app/hooks/useMomentsUploadForm';
import useDeeplink from 'app/hooks/useDeeplink';
import GuestForm from './GuestForm';
import GuestUploadProgressBar from './GuestUploadProgressBar';

import DeleteIcon from '@mui/icons-material/Delete';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LinkIcon from '@mui/icons-material/Link';

import {
  AND_OPERATOR,
} from 'app/hooks/useFilter';

import {
  getVisit
} from 'app/services/guests';

import {
  notifyCustomer,
  getCustomerMoments,
  deleteCustomerMoments
} from 'app/services/customers';

import {
  togglePremiumMoment,
  getMoments
} from 'app/services/moments'

import {
  moments,
  guests
} from 'app/resources';

import { STORY_MOMENTS_DOWNLOAD } from 'app/utils/Constants';

import {
  getLocalizedDate,
  getLocalizedTime,
  getDay
} from 'shared/utils/Utils';

const GuestPanel = props => {
  const {
    guest,
    action,
    onClose,
    onDelete,
    onUploadStart,
    onUploadComplete
  } = props;

  const [visit, setVisit] = useState(null);
  const setupVisit = async () => {
    setVisit(await getVisit(guest.club_med_visit_id));
  }
  useEffect(() => {
    if (guest) {
      setupVisit();
    }
  }, [guest]);

  const {
    formIsOpen,
    onAdd,
    onFormClose,
    onFormSubmit
  } = useMomentsUploadForm({
    onUploadStart,
    onUploadComplete,
    customer: guest
  });

  const deeplink = useDeeplink();

  const user = useSelector(state => state.userReducer.user);
  const account = useSelector(state => state.portalReducer.account);
  const showMomentUpload = !account.feature_flags.disable_upload;

  // if we're creating a guest then no actions panel will be shown
  if (action === 'create') {
    return null;
  }

  let download = () => {
    let link = document.createElement('a');

    link.href = STORY_MOMENTS_DOWNLOAD(guest.story_id);
    link.download = `${guest.id}_guest_moments`;
    link.click();
  }

  // get images for Moments card
  const getImages = async (page = 1, perPage = 100) => {
    let filterModel = {
      guest_id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: guest.id.toString()
      }]
    }

    let params = {
      page,
      per_page: perPage,
      filter_model: filterModel,
      sort_model: []
    }

    let resp = await getMoments(params);

    let images = resp.records.map(moment => ({
      id: moment.id,
      src: moment.preview_url,
      w: moment.width,
      h: moment.height,
      filename: moment.filename,
      premium: moment.premium
    }));

    return images;
  }

  const renderNotificationStatus = (notification) =>{
    switch (notification.status) {
      case 0:
        return 'Pending';
      case 1:
        return 'Sending';
      case 2:
        return 'Delivered';
      case 3:
        return 'Failed';
    }
  }

  const sendNotification = async () => {
    let params = {method: 0, guest_ids:[guest.id], force: true};
    await notifyGuest(params);
  }

  const deleteMoment = async (moment) => {
    let params = {moment_ids: [moment.id]}
    return await deleteGuestMoments(guest.id, params);
  }

  const toggleMoment = async (moment) => {
    let params = {moment_id: moment.id, premium: !moment.premium, guest_id: guest.id}
    return await togglePremiumMoment(moment.id, params);
  }

  const viewMoment = (moment, newTab = false) => {
    let filterModel = {
      id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: moment.id.toString(),
        operator: AND_OPERATOR
      }]
    }

    if (newTab) {
      // return link to set as anchor href with target="_blank"
      return deeplink.getLink(moments, filterModel);
    } else {
      deeplink.load(moments, filterModel);
    }
  }

  const getMomentsLink = () => {
    let filterModel = {
      customer_id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: guest.id.toString(),
        operator: AND_OPERATOR
      }]
    }

    // return link to set as anchor href with target="_blank"
    return deeplink.getLink(moments, filterModel);
  }

  const getGuestsLink = () => {
    let filterModel = {
      club_med_visit_id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: guest.club_med_visit_id.toString(),
        operator: AND_OPERATOR
      }]
    }

    // return link to set as anchor href with target="_blank"
    return deeplink.getLink(guests, filterModel);
  }

  let notificationsMessage = null;
  if (!guest?.notifications || !guest?.notifications.length) {
    notificationsMessage = 'No notifications have been sent.';
  }

  let notificationsDetails = guest.notifications?.map(n => {
    let day = getDay(n.created_at, 'short');
    let date = getLocalizedDate('en-us', n.created_at, 'short');
    let time = getLocalizedTime('en-us', n.created_at, 'short');
    let status = renderNotificationStatus(n);
    let divStyle = { margin: '0.5rem 0' };
    let pStyle = { margin: '0' };

    return {
      label: (
        <div style={divStyle}>
          <p style={pStyle}>{day} {date}</p>
          <p style={pStyle}>{time}</p>
        </div>
      ),
      value: status
    }
  });

  let momentActions = [];
  if (showMomentUpload) {
    momentActions.push(
      { label: 'Upload Moments', callback: onAdd }
    );
  }
  momentActions.push(
    { label: 'View Moments', newTabLink: getMomentsLink() }
  );

  let reservationDetails = [
    { label: 'Room', value: guest.room },
    { label: 'Check In', value: getLocalizedDate(user.locale, guest.check_in) },
    { label: 'Check Out', value: getLocalizedDate(user.locale, guest.check_out) },
  ]
  let otherGuests = [];
  if (visit?.customers.length) {
    otherGuests = visit.customers.filter(c => c.id !== guest.id)
      .map(c => c.name);
  }
  if (visit?.other_guests?.length) {
    otherGuests = otherGuests.concat(visit.other_guests.map(c => c.name));
  }
  if (otherGuests.length) {
    reservationDetails.push({ label: 'Other Guests', value: otherGuests.join(', ') });
  }
  if (visit?.other_rooms?.length) {
    reservationDetails.push({ label: 'Other Rooms', value: visit.other_rooms.join(', ') });
  }

  let cards = [
    {
      label: 'Reservation',
      details: reservationDetails,
      actions: [
        { label: 'View Guests', newTabLink: getGuestsLink() }
      ]
    },
    {
      label: 'Moments',
      getImages,
      imageCount: guest.photo_count,
      imagesPerPage: 100,
      progressBar: <GuestUploadProgressBar guest={guest} inModal={true} />,
      details: [
        { label: 'Moments', value: guest.photo_count },
      ],
      actions: momentActions,
      psActions: [
        { key: "delete", icon: DeleteIcon, onClick: deleteMoment },
        { key: "togglePremium", icon: AttachMoneyIcon, onClick: toggleMoment },
        { key: "link", icon: LinkIcon, onClick: viewMoment },
      ]
    },
    {
      label: 'Download Moments',
      message: 'Download all guest moments.  Includes hidden moments',
      actions: [
        { label: 'Download Moments', callback: download }
      ]
    },
    {
      label: 'Notifications',
      message: notificationsMessage,
      details: notificationsDetails,
      actions: [
        { label: 'Send Notification', callback: sendNotification }
      ]
    }
  ]

  return (
    <div>
      <ResourceModalPanel cards={cards} />
      <CustomerUploadModal
        isOpen={formIsOpen}
        onClose={onFormClose}
        onFormSubmit={onFormSubmit}
        customer={guest}
        center={true}
      />
    </div>
  );
}

const GuestModal = props => {
  const {
    isOpen,
    onClose,
    onFormSubmit,
    onDestroy,
    guest,
    onUploadStart,
    onUploadComplete,
    onCreate,
    center,
    job,
    kioskOptions
  } = props;

  // the guest modal only supports editing existing guests,
  // as all Club Med guests are entered from their system
  if (!guest) {
    return null;
  }

  const action = 'update';
  const title = guest.name;

  const user = useSelector(state => state.userReducer.user);
  const checkIn = getLocalizedDate(user.locale, guest.check_in, 'short');
  const checkOut = getLocalizedDate(user.locale, guest.check_out, 'short');
  const subTitle = `Room ${guest.room}: ${checkIn} - ${checkOut}`;

  const onSubmit = params => onFormSubmit(action, params);

  return (
    <ResourceModal {...{ isOpen, onClose, title, subTitle, center }}>
      <GuestPanel {...{ guest, action, onClose, onUploadStart, onUploadComplete }} />
      <GuestForm {...{ guest, action, onClose, onSubmit, job, kioskOptions }} />
    </ResourceModal>
  );
}

export default GuestModal;
